import { useMemo } from "react";

import type { Direction, SpacetimeResponse, SpacetimeWindow } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import { formatIsoTime } from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { useLines } from "../config/LinesProvider";
import { LinePicker } from "../network/bits";
import { usePolledResource } from "./hooks";
import { PanelHeading } from "./bits";

// P2-38 (contrat v1.14) : fenêtres élargies, défaut 30 min.
const WINDOWS: { id: SpacetimeWindow; label: string }[] = [
  { id: "15m", label: "15 min" },
  { id: "30m", label: "30 min" },
  { id: "1h", label: "1 h" },
  { id: "2h", label: "2 h" },
  { id: "5h", label: "5 h" },
  { id: "day", label: "Jour" },
];

const VB_W = 900;
const VB_H = 340;
const PAD_L = 150;
const PAD_R = 14;
const PAD_T = 12;
const PAD_B = 26;

/**
 * Graphique espace-temps (F1.3) — le seul dessin sur mesure de l'écran, en SVG.
 * Le serveur renvoie, par course, une polyligne `scheduled` et une `realized`
 * DÉJÀ projetées (temps, distanceM le long de la ligne) — ce composant ne fait
 * que les mettre à l'échelle et tracer. `outOfTolerance` désigne des segments
 * de `realized` (indices de points) : on les surtrace en rouge, ce n'est pas
 * une troisième série (D-004).
 */
export function SpacetimeChart({
  lineId,
  direction,
  window: win,
  onWindowChange,
  onLineChange,
  onDirectionChange,
}: {
  lineId: string;
  direction: Direction;
  window: SpacetimeWindow;
  onWindowChange: (w: SpacetimeWindow) => void;
  /** Choisir la ligne / le sens à surveiller — sinon figé sur la ligne de l'écart cliqué. */
  onLineChange: (lineId: string) => void;
  onDirectionChange: (d: Direction) => void;
}) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const lines = useLines();

  const { data, error, loading } = usePolledResource(
    (signal) => backend.api.getSpacetime(networkId, { lineId, direction, window: win }, signal),
    [backend, networkId, lineId, direction, win],
    15_000,
  );

  const geom = useMemo(() => {
    if (!data) return null;
    const t0 = new Date(data.from).getTime();
    const t1 = new Date(data.to).getTime();
    const maxDist = Math.max(1, ...data.stopAxis.map((s) => s.distanceM));
    const x = (iso: string) => PAD_L + ((new Date(iso).getTime() - t0) / (t1 - t0)) * (VB_W - PAD_L - PAD_R);
    const y = (distanceM: number) => PAD_T + (distanceM / maxDist) * (VB_H - PAD_T - PAD_B);
    return { t0, t1, maxDist, x, y };
  }, [data]);

  return (
    <div>
      {/* Choix de la ligne à surveiller (demande utilisateur — jusqu'ici figée
          sur la dernière ligne cliquée dans Avance/Retard, sans autre moyen de
          la changer). Composant partagé avec les vues Réseau, pas de doublon. */}
      <div style={{ marginBottom: 8 }}>
        <LinePicker value={lineId} onChange={onLineChange} withDirection direction={direction} onDirectionChange={onDirectionChange} />
      </div>
      <PanelHeading
        aside={
          <div className="buttons has-addons" style={{ margin: 0, flexWrap: "nowrap" }}>
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                className={`button is-small mono ${w.id === win ? "is-primary" : ""}`}
                style={w.id === win ? { background: "var(--sentinelle-rail)", borderColor: "var(--sentinelle-rail)", color: "#fff" } : undefined}
                onClick={() => onWindowChange(w.id)}
              >
                {w.label}
              </button>
            ))}
          </div>
        }
      >
        Espace-temps — {lines.code(lineId)} {directionLabel(direction)}
        {data ? (
          <span className="ovl" style={{ marginLeft: 10 }}>
            {formatIsoTime(data.from)} → {formatIsoTime(data.to)}
          </span>
        ) : null}
      </PanelHeading>

      {loading && !data ? (
        <p className="has-text-grey is-size-7">Chargement du graphique…</p>
      ) : error && !data ? (
        <p className="has-text-danger is-size-7">{describeError(error)}</p>
      ) : data && geom ? (
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          style={{ width: "100%", height: "auto", display: "block" }}
          role="img"
          aria-label={`Graphique espace-temps de la ligne ${lines.code(lineId)}, ${data.trips.length} courses`}
        >
          {/*
            Une course qui ne fait que recouper la fenêtre est renvoyée avec TOUT
            son horaire théorique, non découpé aux bornes from/to (cf. réponse
            Claude-2 B3.2 — rendu explicitement laissé au frontend). Sans ce
            clip, ses points hors [from, to] traceraient par-dessus les libellés
            d'arrêts ou hors du cadre, surtout sensible en fenêtre « Jour ».
          */}
          <defs>
            <clipPath id="mae-spacetime-plot">
              <rect x={PAD_L} y={PAD_T} width={VB_W - PAD_L - PAD_R} height={VB_H - PAD_T - PAD_B} />
            </clipPath>
          </defs>

          {/* arrêts : lignes horizontales + libellés */}
          {data.stopAxis.map((stop) => {
            const yy = geom.y(stop.distanceM);
            return (
              <g key={stop.stopId}>
                <line x1={PAD_L} y1={yy} x2={VB_W - PAD_R} y2={yy} stroke="var(--bulma-border-weak)" strokeWidth={1} />
                <text
                  x={PAD_L - 8}
                  y={yy + 3}
                  textAnchor="end"
                  fontSize={9}
                  fill="var(--sentinelle-ink-3)"
                  fontFamily="var(--bulma-family-code)"
                >
                  {stop.name}
                </text>
              </g>
            );
          })}

          {/* axes */}
          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={VB_H - PAD_B} stroke="hsl(210,14%,72%)" strokeWidth={1.5} />
          <line x1={PAD_L} y1={VB_H - PAD_B} x2={VB_W - PAD_R} y2={VB_H - PAD_B} stroke="hsl(210,14%,72%)" strokeWidth={1.5} />

          {/* graduations horaires */}
          {timeTicks(geom.t0, geom.t1).map((t) => {
            const xx = geom.x(new Date(t).toISOString());
            return (
              <g key={t}>
                <line x1={xx} y1={PAD_T} x2={xx} y2={VB_H - PAD_B} stroke="var(--bulma-border-weak)" strokeWidth={1} />
                <text x={xx} y={VB_H - PAD_B + 12} textAnchor="middle" fontSize={9} fill="var(--sentinelle-ink-3)" fontFamily="var(--bulma-family-code)">
                  {formatIsoTime(new Date(t).toISOString())}
                </text>
              </g>
            );
          })}

          {/* tracés : théorique (pointillé) puis réalisé (plein), par course — cadrés au clip du plot */}
          <g clipPath="url(#mae-spacetime-plot)">
            {data.trips.map((trip) => (
              <g key={trip.journeyId}>
                <polyline
                  points={pointsOf(trip.scheduled, geom.x, geom.y)}
                  fill="none"
                  stroke="#9aa7b4"
                  strokeWidth={1.4}
                  strokeDasharray="5 4"
                />
                <polyline
                  points={pointsOf(trip.realized, geom.x, geom.y)}
                  fill="none"
                  stroke="var(--sentinelle-rail)"
                  strokeWidth={1.9}
                />
                {trip.outOfTolerance.map((seg, i) => (
                  <polyline
                    key={i}
                    points={pointsOf(trip.realized.slice(seg.fromIndex, seg.toIndex + 1), geom.x, geom.y)}
                    fill="none"
                    stroke="var(--sentinelle-signal)"
                    strokeWidth={2.4}
                  />
                ))}
              </g>
            ))}
          </g>

          {/* repère « maintenant » */}
          <line
            x1={geom.x(data.now)}
            y1={PAD_T}
            x2={geom.x(data.now)}
            y2={VB_H - PAD_B}
            stroke="var(--sentinelle-signal)"
            strokeWidth={1.5}
            strokeDasharray="2 3"
          />
        </svg>
      ) : null}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12, paddingLeft: PAD_L / 6 }}>
        <Legend dash="5 4" color="#9aa7b4" label="Théorique" />
        <Legend color="var(--sentinelle-rail)" label="Réalisé" />
        <Legend color="var(--sentinelle-signal)" label="Hors tolérance" />
        <Legend color="var(--sentinelle-signal)" label="Maintenant" vertical />
      </div>
    </div>
  );
}

function pointsOf(
  points: SpacetimeResponse["trips"][number]["scheduled"],
  x: (iso: string) => number,
  y: (distanceM: number) => number,
): string {
  return points.map((p) => `${x(p.t).toFixed(1)},${y(p.distanceM).toFixed(1)}`).join(" ");
}

function Legend({ color, label, dash, vertical }: { color: string; label: string; dash?: string; vertical?: boolean }) {
  return (
    <span className="ovl" style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
      <span
        style={
          vertical
            ? { width: 0, height: 12, borderLeft: `2px dashed ${color}` }
            : { width: 18, borderTop: `2px ${dash ? "dashed" : "solid"} ${color}` }
        }
        aria-hidden
      />
      {label}
    </span>
  );
}

function directionLabel(direction: Direction): string {
  return direction === 0 ? "sens aller" : "sens retour";
}

function timeTicks(t0: number, t1: number): number[] {
  const span = t1 - t0;
  const step = niceStep(span / 6);
  const ticks: number[] = [];
  const start = Math.ceil(t0 / step) * step;
  for (let t = start; t <= t1; t += step) ticks.push(t);
  return ticks;
}

function niceStep(rough: number): number {
  const candidates = [60_000, 5 * 60_000, 10 * 60_000, 15 * 60_000, 30 * 60_000, 3600_000, 2 * 3600_000, 6 * 3600_000];
  return candidates.find((c) => c >= rough) ?? candidates[candidates.length - 1]!;
}
